<template>
	<div ref="tooltip" class="tooltip-container">
		<div class="tooltip is-fixed" :style="{ left: posX, top: posY }">
			<div class="tooltip-content">
				<slot name="tooltip" />
			</div>
		</div>

		<slot />
	</div>
</template>

<script setup lang="ts">
	import type { GeneralHelper } from '../helper/general';
	import { inject, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';

	defineProps<{
		fixed: boolean,
	}>();

	const tooltip = useTemplateRef<HTMLDivElement>('tooltip');
	const helper = inject<GeneralHelper>('helper')!;
	const posX = ref('0px');
	const posY = ref('0px');
	let updateDebounce: any;

	const update = () => {
		if (!tooltip.value) return;

		const rect = tooltip.value.getBoundingClientRect();

		posY.value = `${Math.floor(rect.top)}px`;
		posX.value = `${Math.floor(rect.left + (tooltip.value.clientWidth / 2))}px`;
	};

	onMounted(() => {
		update();
		updateDebounce = helper.debounce(update, 50);

		const tooltipEl = tooltip.value;
		tooltipEl?.addEventListener('mouseenter', updateDebounce);
	});

	onUnmounted(() => {
		const tooltipEl = tooltip.value;
		tooltipEl?.removeEventListener('mouseenter', updateDebounce);
	});
</script>

<style lang="scss" scoped>
	.tooltip-container {
		position: relative;
		display: inline-block;
		width: 100%;
		height: 100%;
		&:hover {
			.tooltip {
				opacity: 1;
				visibility: visible;
				transition: opacity 0.2s ease-in;
			}
		}

		.tooltip {
			opacity: 0;
			visibility: hidden;
			transition: opacity 0.2s ease-out, visibility 0s linear 0.2s;
			position: absolute;
			background: white;
			color: black;
			padding: 8px 12px;
			border-radius: 6px;
			font-size: 13px;
			box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
			border: 1px solid rgba(0, 0, 0, 0.2);
			z-index: 9999;
			width: 300px;
			max-height: 180px;
			left: 50%;
			transform: translateX(-50%) translateY(calc(-100% - 10px));

			&.is-fixed {
				position: fixed;
			}

			&::after {
				content: "";
				position: absolute;
				top: 100%;
				left: 50%;
				transform: translateX(-50%);
				border-width: 6px;
				border-style: solid;
				border-color: white transparent transparent transparent;
				filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1));
			}

			.tooltip-content {
				display: flex;
				flex-direction: column;
				max-height: 150px;
				overflow-y: auto;
				width: 100%;
				padding: 8px;
				text-wrap: wrap;
			}
		}
	}
</style>
